import { useState, useCallback, useEffect, useRef } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, Bell, Plus, Mail } from "lucide-react";
import { tripsApi, alertsApi, stewardApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { tripStatus } from "@/components/trips/tripStatusView";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import LoadingState from "@/components/shared/LoadingState";
import ErrorState from "@/components/shared/ErrorState";
import EmptyState from "@/components/shared/EmptyState";
import DeleteTripDialog from "@/components/trips/DeleteTripDialog";
import CreateAlertDialog from "@/components/trips/CreateAlertDialog";
import EmailPreviewDialog from "@/components/trips/EmailPreviewDialog";
import AlertHistoryView from "@/components/trips/AlertHistoryView";
import TripScheduleControls from "@/components/trips/TripScheduleControls";
import SearchLogView from "@/components/trips/SearchLogView";
import CheckPricesButton from "@/components/trips/CheckPricesButton";
import CompanionStrip from "@/components/trips/CompanionStrip";
import TripBriefing from "@/components/trips/TripBriefing";
import TravelGroupPanel from "@/components/trips/TravelGroupPanel";
import { type UnifiedAddStopRequest } from "@/components/trips/unified/UnifiedItinerary";
import TripPlanViews from "@/components/trips/unified/TripPlanViews";
import { ITEM_TYPES } from "@/components/trips/itinerary/itinerary-utils";
import StewardWorkspace, {
  type StewardSeed,
} from "@/components/trips/steward/StewardWorkspace";
import StewardGapBanner from "@/components/trips/steward/StewardGapBanner";
import StewardBackboneCard from "@/components/trips/steward/StewardBackboneCard";
import type { Route, Alert, ItineraryItemType } from "@/lib/types";

/* Lightweight info about a selected flight. The unified itinerary no longer
   uses optimistic local selections, but the (now-unmounted) plan components
   still import this type, so it stays exported to keep their imports valid. */
export interface FlightInfo {
  flightId: string;
  price: number;
  airline: string;
  flightNumber: string;
  departureTime?: string;
  arrivalTime?: string;
  stops?: number;
  durationMinutes?: number;
}

export default function TripDetail() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [createAlertOpen, setCreateAlertOpen] = useState(false);
  const [emailPreviewOpen, setEmailPreviewOpen] = useState(false);

  // When we arrive here straight from creating a trip, the server has already
  // started a fare check and handed us its jobId via ?check=…. Capture it once
  // so the Check Prices button mounts already polling — fares appear ASAP — then
  // strip the param so a reload doesn't resurrect a stale job.
  //
  // Email deep-links supply ?leg=<routeId> or ?view=fares. Capture once on
  // mount, strip them from the URL, then scroll after trip data loads (see the
  // effect below that depends on `trip`).
  const [searchParams, setSearchParams] = useSearchParams();
  const [autoCheckJobId] = useState<string | null>(() => searchParams.get("check"));
  const [deepLinkLeg] = useState<string | null>(() => searchParams.get("leg"));
  const [deepLinkView] = useState<string | null>(() => searchParams.get("view"));

  // Phase-4 email deep-link: /trips/:id?addStop=1&after=<outbound|return>&date=<YYYY-MM-DD>&type=<…>
  // Capture once on mount; we scroll to + auto-open the add dialog after trip data
  // loads, then strip the params (replace:true). `after` is captured for parity
  // with the email scheme; the actionable bits are `date` (day to prefill) + `type`.
  const [addStopRequest, setAddStopRequest] = useState<UnifiedAddStopRequest | null>(
    () => {
      if (searchParams.get("addStop") !== "1") return null;
      const date = searchParams.get("date") || undefined;
      const rawType = searchParams.get("type");
      const type =
        rawType && (ITEM_TYPES as string[]).includes(rawType)
          ? (rawType as ItineraryItemType)
          : undefined;
      return { date, type };
    },
  );

  // Steward deep link: /trips/:id?steward=1 (same capture-once pattern as
  // ?addStop=1) so emails and the completeness coach's lodging nudge can land
  // the traveler straight in the workspace.
  const [stewardOpen, setStewardOpen] = useState(
    () => searchParams.get("steward") === "1",
  );
  const [stewardSeed, setStewardSeed] = useState<StewardSeed | null>(null);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (next.has("steward")) { next.delete("steward"); changed = true; }
    if (next.has("check")) { next.delete("check"); changed = true; }
    if (next.has("leg"))   { next.delete("leg");   changed = true; }
    if (next.has("view"))  { next.delete("view");  changed = true; }
    if (next.has("addStop")) { next.delete("addStop"); changed = true; }
    if (next.has("after"))   { next.delete("after");   changed = true; }
    if (next.has("date"))    { next.delete("date");    changed = true; }
    if (next.has("type"))    { next.delete("type");    changed = true; }
    if (changed) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["trip", id],
    queryFn: () => tripsApi.getById(id!),
    enabled: !!id,
  });

  const { data: alertsData } = useQuery({
    queryKey: ["alerts", id],
    queryFn: () => alertsApi.getByTrip(id!),
    enabled: !!id,
  });

  // Steward's opener. Cached server-side on the conversation row, so this is a
  // cheap read after the first visit. `opener: null` = nothing worth saying =
  // no banner at all; the query never blocks or errors the page.
  const { data: steward } = useQuery({
    queryKey: ["stewardOpener", id],
    queryFn: () => stewardApi.opener(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const openSteward = useCallback((seed?: StewardSeed) => {
    setStewardSeed(seed ?? null);
    setStewardOpen(true);
  }, []);

  /** The gap banner's chips are bare sentences, with no add-slot behind them. */
  const openStewardWith = useCallback(
    (message?: string) => openSteward(message ? { message } : undefined),
    [openSteward],
  );

  const trip = data?.trip;

  // Scroll target — the unified itinerary surface.
  const planRef = useRef<HTMLDivElement | null>(null);

  // After a Check Prices run completes, pull fresh data so the unified
  // itinerary reflects the new fares without a manual reload. The plan surface
  // reads ["tripPlan", id]; we also refresh ["trip"] and every route's flights /
  // recommendation / focus set (other surfaces + the plan's matrix all derive
  // from these).
  const handleCheckComplete = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["trip", id] });
    queryClient.invalidateQueries({ queryKey: ["tripPlan", id] });
    const tripRoutes: Route[] = data?.trip?.routes || [];
    tripRoutes.forEach((route: Route) => {
      queryClient.invalidateQueries({ queryKey: ["flights", route.id] });
      queryClient.invalidateQueries({ queryKey: ["recommendation", route.id] });
      queryClient.invalidateQueries({ queryKey: ["focusSet", route.id] });
    });
  }, [queryClient, id, data]);

  // Deep-link scroll: once trip data resolves, scroll to the target element.
  // Small timeout lets React flush the tree so the element exists in the DOM.
  useEffect(() => {
    if (!trip) return;
    const timer = setTimeout(() => {
      // ?addStop=1 / ?view=fares both bring the unified itinerary into view;
      // ?leg=<routeId> deep-links to that leg's section inside it.
      if (deepLinkLeg) {
        document.getElementById("leg-" + deepLinkLeg)?.scrollIntoView({ behavior: "smooth", block: "center" });
      } else if (addStopRequest || deepLinkView === "fares") {
        document.getElementById("unified-itinerary")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [trip, deepLinkLeg, deepLinkView, addStopRequest]);

  if (isLoading) return <LoadingState message="Loading trip..." />;
  if (error) return <ErrorState message="Failed to load trip" onRetry={() => refetch()} />;
  if (!trip) return <ErrorState message="Trip not found" />;

  const alerts: Alert[] = alertsData?.alerts || trip.alerts || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight">{trip.name}</h1>
            {/* One rule, one place (tripStatusView): finished travel reads
                "Past", never the Active/Paused watch switch. */}
            <Badge
              title={tripStatus(trip).title}
              className={
                tripStatus(trip).tone === "active"
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-muted text-muted-foreground border-border"
              }
            >
              {tripStatus(trip).label}
            </Badge>
          </div>
          {trip.description && (
            <p className="text-muted-foreground">{trip.description}</p>
          )}
        </div>
        <div className="flex flex-wrap items-start gap-2">
          <CheckPricesButton tripId={id!} onComplete={handleCheckComplete} autoJobId={autoCheckJobId} />
          <Button variant="outline" size="sm" onClick={() => setEmailPreviewOpen(true)}>
            <Mail className="h-4 w-4 mr-1.5" /> Preview email
          </Button>
          <Button variant="secondary" size="sm" asChild>
            <Link to={"/trips/" + id + "/edit"}>
              <Pencil className="h-4 w-4 mr-1.5" /> Edit
            </Link>
          </Button>
          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)}>
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete
          </Button>
        </div>
      </div>

      {/* Steward's proactive hook: the unplanned stretch between the trip's
          anchors (e.g. the days between disembarking and the flight home).
          Renders ONLY when the server returned an opener — no gap, no banner. */}
      {steward?.opener && (
        <StewardGapBanner
          opener={steward.opener}
          chips={steward.chips}
          gap={steward.gap}
          changes={steward.changes}
          onOpen={openStewardWith}
        />
      )}

      {/* The trip the traveler ALREADY described — the cruise, the pier-to-airport
          move its disembark date implies, the shoulder day before sailing — offered
          as itinerary rows. Sits directly under the gap banner because it is the
          same conversation: the banner counts the unplanned days, this writes down
          the planned ones. Renders nothing when there is nothing left to offer. */}
      <StewardBackboneCard tripId={id!} />

      {/* Companion strip — who's coming on this trip. Resolves soft-matched
          mentions from the description; resolved companions link to their profile. */}
      <CompanionStrip tripId={id!} description={trip.description} />

      {/* Travel group — links co-travelers from other accounts; shows their
          arrival/departure windows to enable flight coordination. */}
      <TravelGroupPanel tripId={id!} tripName={trip.name} />


      {/* Trip Intel briefing — grounded event/destination facts, shown prominently
          the first time then demoted to a collapsed note once seen. */}
      <TripBriefing tripId={id!} />

      {/* The trip, two ways. ITINERARY is the default: a day-by-day, collapsible,
          printable document — the thing you carry. FARES is the previous
          leg-first surface, unchanged, where the price detail that matters
          during a fare search still lives.
          id="unified-itinerary" anchors deep-links (?addStop / ?view=fares);
          ?leg=<routeId> and ?view=fares open the Fares tab so the per-leg
          id="leg-<routeId>" the scroll effect below looks for is mounted. */}
      <div id="unified-itinerary" ref={planRef}>
        <TripPlanViews
          tripId={id!}
          initialView={
            deepLinkLeg || deepLinkView === "fares" ? "fares" : "itinerary"
          }
          addStopRequest={addStopRequest}
          onAddStopConsumed={() => setAddStopRequest(null)}
          onAskSteward={openSteward}
          stewardGap={steward?.gap}
        />
      </div>

      {/* Schedule Controls - collapsed by default */}
      <TripScheduleControls
        tripId={id!}
        active={trip.active}
        checkIntervalHours={trip.check_interval_hours || 8}
        emailFrequency={trip.email_frequency || "always"}
        past={tripStatus(trip).past}
        travelEndDate={trip.travel_end_date ?? null}
      />


      <Separator />

      {/* Alerts */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Alerts</h2>
          <Button variant="outline" size="sm" onClick={() => setCreateAlertOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" /> Add Alert
          </Button>
        </div>
        {alerts.length === 0 ? (
          <EmptyState icon={Bell} title="No alerts configured" description="Add alerts to get notified of price changes" />
        ) : (
          <AlertHistoryView tripId={id!} />
        )}
      </section>

      <Separator />
      <SearchLogView tripId={id!} />

      {/* The workspace itself — a right-side sheet, opened from the banner, the
          AddStopMenu "Ask Steward" row, or the ?steward=1 deep link. */}
      <StewardWorkspace
        open={stewardOpen}
        onOpenChange={setStewardOpen}
        tripId={id!}
        tripName={trip.name}
        opener={steward?.opener}
        openerChips={steward?.chips}
        conversationId={steward?.conversationId}
        seed={stewardSeed}
        onSeedConsumed={() => setStewardSeed(null)}
      />

      <DeleteTripDialog tripId={id!} tripName={trip.name} open={deleteOpen} onOpenChange={setDeleteOpen} />
      <CreateAlertDialog tripId={id!} open={createAlertOpen} onOpenChange={setCreateAlertOpen} />
      <EmailPreviewDialog tripId={id!} open={emailPreviewOpen} onOpenChange={setEmailPreviewOpen} />
    </div>
  );
}
